import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import Header from '../components/layout/Header'
import MarketSelectorSheet from '../components/trade/MarketSelectorSheet'
import { mountLivePriceChart } from '../components/trade/livePriceChart'
import { mountOrderFormControls } from '../components/trade/orderFormControls'
import { mountLiveMarketData } from '../components/trade/tradeLiveData'
import { usePageCss } from '../hooks/usePageCss'
import { useWalletAuth } from '../context/WalletAuthProvider'
import TradeMarkup from '../components/trade/generated/TradeMarkup'
import marketFormHtml from '../components/trade/generated/trade_order_form_market.html?raw'
import limitFormHtml from '../components/trade/generated/trade_order_form_limit.html?raw'
import {
  positionsEmptyHtml,
  startPositionsSignal,
  stopPositionsSignal,
} from '../components/trade/positionsEmpty'
import './trade-mobile.css'

const TAB_ACTIVE =
  "relative flex items-center justify-center gap-1 text-sm/sm whitespace-nowrap transition-colors rounded-card font-medium text-ink shrink-0 px-2.5 py-1 max-mobile:px-3 max-mobile:py-2 max-mobile:before:absolute max-mobile:before:inset-x-0 max-mobile:before:-inset-y-2 max-mobile:before:content-['']"
const TAB_IDLE =
  "relative flex items-center justify-center gap-1 text-sm/sm whitespace-nowrap transition-colors rounded-card font-normal hover:text-ink active:text-ink shrink-0 hover:bg-ink/5 px-2.5 py-1 max-mobile:px-3 max-mobile:py-2 text-dim max-mobile:before:absolute max-mobile:before:inset-x-0 max-mobile:before:-inset-y-2 max-mobile:before:content-['']"
const SIDE_BTN =
  'z-10 flex-1 cursor-pointer rounded-none leading-sm transition-colors disabled:cursor-default disabled:opacity-50'
const SIDE_BUY_ACTIVE = `${SIDE_BTN} font-medium text-[oklch(0.367_0.009_261)]`
const SIDE_BUY_IDLE = `${SIDE_BTN} trade-side-buy-idle text-dim`
const SIDE_SELL_ACTIVE = `${SIDE_BTN} trade-side-sell-active font-medium text-[rgb(255,255,255)]`
const SIDE_SELL_IDLE = `${SIDE_BTN} text-dim hover:text-down active:text-down`
const TAB_PILL =
  'absolute inset-0 rounded-card bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] light:shadow-[inset_0_0_0_0.5px_var(--ring-crisp)]!'

const ORDER_TAB_ACTIVE =
  'relative flex items-center justify-center gap-1 text-sm/sm whitespace-nowrap transition-colors rounded-card font-medium text-ink shrink-0 px-2.5 py-1 max-mobile:py-1.5'
const ORDER_TAB_IDLE =
  'relative flex items-center justify-center gap-1 text-sm/sm whitespace-nowrap transition-colors rounded-card font-normal hover:text-ink active:text-ink shrink-0 hover:bg-ink/5 px-2.5 py-1 max-mobile:py-1.5 text-ink/80'

const CTA_DISCONNECTED =
  'group inline-flex shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80 disabled:hover:bg-ink disabled:active:bg-ink light:shadow-(--btn-primary-shadow) h-8 gap-2 p-2 max-mobile:min-h-(--control-h-lg) w-full'
const CTA_BUY =
  'group inline-flex shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 h-8 gap-2 p-2 max-mobile:min-h-(--control-h-lg) w-full bg-[rgb(var(--buy-rgb))] text-[var(--cta-label-buy,#003e13)] hover:opacity-90 active:opacity-80'
const CTA_SELL =
  'group inline-flex shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 h-8 gap-2 p-2 max-mobile:min-h-(--control-h-lg) w-full bg-[rgb(var(--sell-rgb))] text-[var(--cta-label-sell,#fff)] hover:opacity-90 active:opacity-80'

const CHEVRON_MD = `<span data-icon="true" class="flex shrink-0 items-center justify-center size-4 text-ink/50 transition-all"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" class="size-full"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"></path></svg></span>`

const MOBILE_CTA_BUY =
  'group inline-flex w-fit cursor-pointer items-center justify-center truncate rounded-pill transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 border-[0.5px] border-[rgba(var(--buy-rgb),var(--cta-fill,0.2))] bg-[rgba(var(--buy-rgb),var(--cta-fill,0.2))] text-[rgb(0,62,19)] shadow-[0px_0px_0px_0.5px_var(--color-bg)] hover:opacity-90 active:opacity-80 disabled:hover:opacity-50 disabled:active:opacity-50 gap-2 p-2 max-mobile:min-h-(--control-h-lg) h-11 min-h-11 flex-1 text-md font-semibold shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp)]'
const MOBILE_CTA_SELL =
  'group inline-flex w-fit cursor-pointer items-center justify-center truncate rounded-pill transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 border-[0.5px] border-[rgba(var(--sell-rgb),var(--cta-fill,0.2))] bg-[rgba(var(--sell-rgb),var(--cta-fill,0.2))] text-[rgb(255,255,255)] shadow-[0px_0px_0px_0.5px_var(--color-bg)] hover:opacity-90 active:opacity-80 disabled:hover:opacity-50 disabled:active:opacity-50 gap-2 p-2 max-mobile:min-h-(--control-h-lg) h-11 min-h-11 flex-1 text-md font-semibold shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp)]'

const PANEL_IDS = [
  'market-ticker',
  'chart',
  'orderbook',
  'order-form',
  'account',
  'positions',
]

const ACCOUNT_DISCONNECTED = `<div class="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
  <p class="text-sm font-medium text-ink">Not connected</p>
  <span class="flex flex-col items-center gap-6 max-mobile:m-auto text-sm/sm text-muted"><span class="max-mobile:m-auto">Please
    <span class="pointer-events-none">connect</span>
    your wallet</span><button type="button" data-testid="account-connect-wallet-button" class="group inline-flex w-fit shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80 disabled:hover:bg-ink disabled:active:bg-ink light:shadow-(--btn-primary-shadow) h-8 gap-2 p-2 max-mobile:min-h-(--control-h-lg) rounded-card px-4">
    <span>Connect Wallet</span>
  </button></span>
</div>`

const ACCOUNT_CONNECTED = `<div class="flex flex-1 flex-col gap-3 p-3" data-testid="account-connected-panel">
  <div class="flex w-full justify-between"><p class="whitespace-nowrap text-dim text-sm">Account Equity</p><div class="text-ink tabular-nums text-sm">$0.00</div></div>
  <div class="flex w-full justify-between"><p class="whitespace-nowrap text-dim text-sm">Available Balance</p><div class="text-ink tabular-nums text-sm">$0.00</div></div>
  <div class="flex w-full justify-between"><p class="whitespace-nowrap text-dim text-sm">Used Margin</p><div class="text-ink tabular-nums text-sm">$0.00</div></div>
  <button type="button" data-testid="account-deposit-button" class="group inline-flex w-full shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80 h-8 gap-2 p-2 mt-auto">
    <span>Deposit</span>
  </button>
</div>`

function truncateAddress(address) {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`.toUpperCase()
}

function buttonLabel(button) {
  const visible = button.querySelector(
    '.col-start-1.row-start-1:not([aria-hidden])'
  )
  if (visible?.textContent) {
    return visible.textContent.replace(/\s+/g, ' ').trim()
  }
  return (button.textContent || '').replace(/\s+/g, ' ').trim()
}

function setTabPressed(button, pressed, { activeClass = TAB_ACTIVE, idleClass = TAB_IDLE } = {}) {
  const extras = [...button.classList].filter(
    (name) => name === 'trade-mobile-only' || name === 'trade-desktop-only'
  )
  button.setAttribute('aria-pressed', pressed ? 'true' : 'false')
  button.className = pressed ? activeClass : idleClass
  extras.forEach((name) => button.classList.add(name))
  const pill = [...button.children].find((el) =>
    el.classList?.contains('absolute')
  )
  if (pressed && !pill) {
    const span = document.createElement('span')
    span.className = TAB_PILL
    span.setAttribute('style', 'filter: blur(0px); transform: none; opacity: 1')
    button.insertBefore(span, button.firstChild)
  } else if (!pressed && pill) {
    pill.remove()
  }
}

function makeMobileTab(source, label, view) {
  const button = source.cloneNode(true)
  button.setAttribute('data-mobile-view-tab', view)
  button.querySelectorAll('.col-start-1.row-start-1').forEach((el) => {
    el.textContent = label
  })
  setTabPressed(button, false, { idleClass: TAB_IDLE })
  button.classList.add('trade-mobile-only')
  return button
}

function clickNamedButton(root, name) {
  if (!root) return
  root.querySelectorAll('button').forEach((button) => {
    if (buttonLabel(button) === name) button.click()
  })
}

function getTradeState(page) {
  return {
    orderType: page.dataset.orderType === 'limit' ? 'limit' : 'market',
    side: page.dataset.orderSide === 'sell' ? 'sell' : 'buy',
    connected: page.dataset.walletConnected === 'true',
  }
}

function placeOrderLabel(orderType, side) {
  const action = side === 'sell' ? 'SELL' : 'BUY'
  const type = orderType === 'limit' ? 'LIMIT' : 'MARKET'
  return `PLACE ${action} ${type}`
}

function syncPlaceOrderCta(page) {
  const { orderType, side, connected } = getTradeState(page)
  const cta =
    page.querySelector('[data-testid="place-order-button"]') ||
    page.querySelector('[data-testid="connect-wallet-to-trade-button"]')
  if (!cta) return
  const label = cta.querySelector('span') || cta
  if (connected) {
    cta.className = side === 'sell' ? CTA_SELL : CTA_BUY
    cta.setAttribute('data-testid', 'place-order-button')
    label.textContent = placeOrderLabel(orderType, side)
  } else {
    cta.className = CTA_DISCONNECTED
    cta.setAttribute('data-testid', 'connect-wallet-to-trade-button')
    label.textContent = 'Connect Wallet to Trade'
  }
}

function applyOrderSide(page, side) {
  page.dataset.orderSide = side
  const buy = page.querySelector('[data-testid="order-buy-button"]')
  const sell = page.querySelector('[data-testid="order-sell-button"]')
  const slider = buy?.parentElement?.querySelector('.absolute.inset-y-0')
  if (buy && sell) {
    buy.removeAttribute('style')
    sell.removeAttribute('style')
    if (side === 'buy') {
      buy.className = SIDE_BUY_ACTIVE
      sell.className = SIDE_SELL_IDLE
      if (slider) {
        slider.className =
          'absolute inset-y-0 w-1/2 transition-all light:shadow-[inset_0_0_0_0.5px_var(--ring-solid)] left-0 rounded-l-card bg-[rgba(var(--buy-rgb),var(--side-fill,0.2))]'
      }
    } else {
      sell.className = SIDE_SELL_ACTIVE
      buy.className = SIDE_BUY_IDLE
      if (slider) {
        slider.className =
          'absolute inset-y-0 w-1/2 transition-all light:shadow-[inset_0_0_0_0.5px_var(--ring-solid)] left-1/2 rounded-r-card bg-[rgba(var(--sell-rgb),var(--side-fill,0.2))]'
      }
    }
  }

  const placeOrder = page.querySelector('#place-order')
  placeOrder?.querySelectorAll('[class*="--buy-rgb"], [class*="--sell-rgb"]').forEach((el) => {
    if (side === 'buy') {
      el.className = el.className.replaceAll('--sell-rgb', '--buy-rgb')
    } else {
      el.className = el.className.replaceAll('--buy-rgb', '--sell-rgb')
    }
  })
  syncPlaceOrderCta(page)
}

function applyOrderType(page, orderType) {
  page.dataset.orderType = orderType
  const marketBtn = page.querySelector('[data-testid="select-order-type-market"]')
  const limitBtn = page.querySelector('[data-testid="select-order-type-limit"]')
  if (marketBtn) {
    setTabPressed(marketBtn, orderType === 'market', {
      activeClass: ORDER_TAB_ACTIVE,
      idleClass: ORDER_TAB_IDLE,
    })
  }
  if (limitBtn) {
    setTabPressed(limitBtn, orderType === 'limit', {
      activeClass: ORDER_TAB_ACTIVE,
      idleClass: ORDER_TAB_IDLE,
    })
  }

  const formHost = page.querySelector('#place-order')
  if (!formHost) return
  const current = formHost.querySelector('.flex.flex-1.flex-col.gap-2\\.5.overflow-y-scroll')
  // class selector with escaped dots can fail — find by structure
  const scrollBody = [...formHost.querySelectorAll('div')].find((el) =>
    el.className?.includes?.('flex flex-1 flex-col gap-2.5 overflow-y-scroll')
  )
  const html = orderType === 'limit' ? limitFormHtml : marketFormHtml
  if (scrollBody) {
    const wrap = document.createElement('div')
    wrap.innerHTML = html.trim()
    const next = wrap.firstElementChild
    if (next) scrollBody.replaceWith(next)
  } else if (current) {
    current.outerHTML = html
  }
  syncPlaceOrderCta(page)
  applyOrderSide(page, getTradeState(page).side)
  mountOrderFormControls(page)
}

function applyWalletState(page, { isConnected, userAddress }) {
  page.dataset.walletConnected = isConnected ? 'true' : 'false'

  const walletStatus = page.querySelector('[data-testid="wallet-status"]')
  if (walletStatus) {
    const iconHtml = `<span data-icon="true" class="flex shrink-0 items-center justify-center h-2 w-0.5 text-current"><svg viewBox="0 0 2 8" xmlns="http://www.w3.org/2000/svg" fill="none" class="size-full"><rect width="2" height="8" rx="1" fill="currentColor"></rect></svg></span>`
    if (isConnected && userAddress) {
      walletStatus.className =
        'flex items-center gap-1 text-up-strong cursor-pointer hover:opacity-80'
      walletStatus.removeAttribute('style')
      walletStatus.innerHTML = `${iconHtml}${truncateAddress(userAddress)}`
    } else {
      walletStatus.className =
        'flex items-center gap-1 text-down cursor-pointer hover:opacity-80'
      walletStatus.removeAttribute('style')
      walletStatus.innerHTML = `${iconHtml}WALLET NOT CONNECTED`
    }
  }

  const accountHost = page.querySelector('[data-trade-panel="account"] .rounded-modal')
  if (accountHost) {
    accountHost.innerHTML = isConnected ? ACCOUNT_CONNECTED : ACCOUNT_DISCONNECTED
  }

  applyPositionsContent(page)
  syncPlaceOrderCta(page)
}

function applyPositionsContent(page) {
  const ordersTable = page.querySelector('[data-testid="user-orders-table"]')
  if (!ordersTable) return
  stopPositionsSignal(page)
  const connected = page.dataset.walletConnected === 'true'
  const tab = page.dataset.positionsTab || 'Positions'
  ordersTable.innerHTML = positionsEmptyHtml({ connected, tab })
  if (!connected) startPositionsSignal(page, ordersTable)
}

function selectPositionsTab(page, button) {
  const panel = page.querySelector('[data-trade-panel="positions"]')
  const tabs = panel?.querySelectorAll('.flex.items-stretch.gap-1 > button')
  if (!tabs?.length || !button) return
  tabs.forEach((tab) => setTabPressed(tab, tab === button))
  page.dataset.positionsTab = buttonLabel(button)
  applyPositionsContent(page)
}

function dismissBillboard(event, page) {
  const dismiss = event.target.closest('[aria-label="Dismiss announcement"]')
  if (!dismiss) return false
  event.preventDefault()
  const banner = dismiss.closest('[style*="grid-template-rows"]')
  const windowEl = banner?.querySelector('[style*="--billboard-window"]')
  const padWrap = banner?.querySelector('.relative.w-full.px-1')
  const occupied =
    (windowEl?.getBoundingClientRect().height || 32) +
    (padWrap ? parseFloat(getComputedStyle(padWrap).paddingTop) || 0 : 0)
  if (banner) banner.style.gridTemplateRows = '0fr'
  if (padWrap) padWrap.style.paddingTop = '0px'
  const shell = page?.querySelector(
    '.absolute.inset-0.flex.flex-col.overflow-hidden'
  )
  if (shell && page.dataset.billboardDismissed !== 'true') {
    page.dataset.billboardDismissed = 'true'
    const current =
      parseFloat(getComputedStyle(shell).getPropertyValue('--trade-pad-top')) ||
      87
    shell.style.setProperty(
      '--trade-pad-top',
      `${Math.max(0, current - occupied)}px`
    )
    fitTradeLayout(page)
    window.setTimeout(() => fitTradeLayout(page), 220)
  }
  return true
}

/** Desktop grid from app.lighter.xyz/trade/LIT at 1432×1008 (grid 1004px). */
const DESKTOP_LAYOUT = {
  'market-ticker': { top: 0, height: 32 },
  chart: { top: 36, height: 572 },
  orderbook: { top: 0, height: 608 },
  'order-form': { top: 0, height: 608 },
  account: { top: 612, height: 392 },
  positions: { top: 612, height: 392 },
}
const DESKTOP_BASE_HEIGHT = 1004

function fitTradeLayout(page) {
  if (window.matchMedia('(max-width: 767px)').matches) return

  const grid = page.querySelector('.react-grid-layout')
  if (!grid) return

  const items = [...grid.querySelectorAll(':scope > .react-grid-item')]
  items.forEach((item, index) => {
    const panelId = item.dataset.tradePanel || PANEL_IDS[index]
    const preset = panelId ? DESKTOP_LAYOUT[panelId] : null
    if (!preset) return
    item.dataset.baseTop = String(preset.top)
    item.dataset.baseHeight = String(preset.height)
    item.style.top = `${preset.top}px`
    item.style.height = `${preset.height}px`
  })

  grid.style.height = `${DESKTOP_BASE_HEIGHT}px`
}

function setupTradeDesktop(page) {
  if (page.dataset.tradeDesktopReady === 'true') return () => {}
  page.dataset.tradeDesktopReady = 'true'
  page.dataset.orderType = 'market'
  page.dataset.orderSide = 'buy'
  if (!page.dataset.positionsTab) page.dataset.positionsTab = 'Positions'

  const grid = page.querySelector('.react-grid-layout')
  if (grid) {
    const items = [...grid.querySelectorAll(':scope > .react-grid-item')]
    items.forEach((item, index) => {
      if (PANEL_IDS[index]) item.dataset.tradePanel = PANEL_IDS[index]
    })
  }

  const onResize = () => fitTradeLayout(page)
  applyOrderSide(page, page.dataset.orderSide === 'sell' ? 'sell' : 'buy')
  fitTradeLayout(page)
  requestAnimationFrame(() => fitTradeLayout(page))
  const shell = page.querySelector(
    '.absolute.inset-0.flex.flex-col.overflow-hidden'
  )
  const resizeObserver =
    typeof ResizeObserver !== 'undefined' && shell
      ? new ResizeObserver(() => fitTradeLayout(page))
      : null
  resizeObserver?.observe(shell)
  window.addEventListener('resize', onResize)

  const onClick = (event) => {
    if (dismissBillboard(event, page)) return

    const positionsTab = event.target.closest(
      '[data-trade-panel="positions"] .flex.items-stretch.gap-1 > button'
    )
    if (positionsTab) {
      event.preventDefault()
      selectPositionsTab(page, positionsTab)
      return
    }

    const whatsNew = event.target.closest('[data-testid="footer-whats-new"]')
    if (whatsNew) {
      event.preventDefault()
      window.dispatchEvent(new Event('lighter:open-whats-new'))
      return
    }

    const marketTab = event.target.closest('[data-testid="select-order-type-market"]')
    if (marketTab) {
      event.preventDefault()
      event.stopPropagation()
      applyOrderType(page, 'market')
      return
    }
    const limitTab = event.target.closest('[data-testid="select-order-type-limit"]')
    if (limitTab) {
      event.preventDefault()
      event.stopPropagation()
      applyOrderType(page, 'limit')
      return
    }

    const buyBtn = event.target.closest('[data-testid="order-buy-button"]')
    if (buyBtn) {
      event.preventDefault()
      applyOrderSide(page, 'buy')
      return
    }
    const sellBtn = event.target.closest('[data-testid="order-sell-button"]')
    if (sellBtn) {
      event.preventDefault()
      applyOrderSide(page, 'sell')
      return
    }

    const placeBtn = event.target.closest('[data-testid="place-order-button"]')
    if (placeBtn) {
      event.preventDefault()
      return
    }

    const connect = event.target.closest(
      '[data-testid="connect-wallet-to-trade-button"], [data-testid="account-connect-wallet-button"], [data-testid="wallet-status"], .cursor-pointer.underline'
    )
    if (connect) {
      const { connected } = getTradeState(page)
      if (connected && connect.matches('[data-testid="wallet-status"]')) return
      if (
        connect.matches('[data-testid="connect-wallet-to-trade-button"]') ||
        connect.matches('[data-testid="account-connect-wallet-button"]') ||
        connect.classList.contains('underline') ||
        (connect.matches('[data-testid="wallet-status"]') && !connected)
      ) {
        event.preventDefault()
        window.dispatchEvent(new Event('lighter:open-connect'))
      }
    }
  }

  page.addEventListener('click', onClick)
  return () => {
    page.removeEventListener('click', onClick)
    window.removeEventListener('resize', onResize)
    resizeObserver?.disconnect()
    delete page.dataset.tradeDesktopReady
  }
}

function setupTradeMobile(page) {
  if (page.dataset.tradeMobileReady === 'true') return () => {}

  page.dataset.tradeMobileReady = 'true'
  page.dataset.mobileView = 'price'
  if (!page.dataset.positionsTab) page.dataset.positionsTab = 'Positions'

  const items = page.querySelectorAll('.react-grid-layout > .react-grid-item')
  items.forEach((item, index) => {
    if (PANEL_IDS[index]) item.setAttribute('data-trade-panel', PANEL_IDS[index])
  })

  const positionsPanel = page.querySelector('[data-trade-panel="positions"]')
  if (positionsPanel) {
    positionsPanel
      .querySelectorAll('.flex.items-stretch.gap-1 > button')
      .forEach((button) => {
        if (buttonLabel(button) === 'Strategies') {
          button.classList.add('trade-desktop-only')
        }
      })
  }

  const marketBar = [...page.querySelectorAll('[data-trade-panel="chart"] .flex')].find(
    (el) => typeof el.className === 'string' && el.className.includes('h-[40px]')
  )
  const price = '3.3985'
  const change = '-1.12%'
  const down = change.trim().startsWith('-')

  if (marketBar) {
    marketBar.className = 'flex items-center justify-between p-1.5'
  }

  if (marketBar && !page.querySelector('[data-testid="mobile-market-stats-toggle"]')) {
    const ticker = document.createElement('div')
    ticker.className =
      'trade-mobile-only flex shrink cursor-pointer items-center gap-2 overflow-hidden text-ink'
    ticker.dataset.testid = 'mobile-market-stats-toggle'
    ticker.setAttribute('role', 'button')
    ticker.setAttribute('aria-expanded', 'false')
    ticker.tabIndex = 0
    ticker.innerHTML = `<span class="text-md leading-none text-ink tabular-nums" data-testid="mobile-last-price">${price}</span><span class="text-md leading-none tabular-nums ${down ? 'text-down' : 'text-up'}" data-testid="mobile-last-change">${change}</span>${CHEVRON_MD}`
    marketBar.appendChild(ticker)
  }
  if (
    marketBar &&
    !marketBar.nextElementSibling?.classList.contains('trade-mobile-market-rule')
  ) {
    const rule = document.createElement('div')
    rule.className = 'trade-mobile-only trade-mobile-market-rule w-full border-b border-b-line'
    marketBar.after(rule)
  }

  const statsWrap = page.querySelector(
    '.relative.w-full.max-w-full.overflow-x-hidden'
  )
  statsWrap?.classList.add('trade-mobile-stats-panel')

  const tabRow = page.querySelector(
    '#tv-chart-container > .w-full.text-xs > div'
  )
  page
    .querySelectorAll('#tv-chart-container > .w-full.text-xs > div')
    .forEach((el) => {
      if (
        !el.children.length &&
        typeof el.className === 'string' &&
        el.className.includes('border-b')
      ) {
        el.classList.add('trade-mobile-gap-kill')
      }
    })
  const leftGroup = tabRow?.querySelector('.flex.items-stretch.gap-1')
  const rightGroup = leftGroup?.nextElementSibling
  rightGroup?.classList.add('trade-desktop-only')

  const existingTabs = leftGroup
    ? [...leftGroup.querySelectorAll(':scope > button')]
    : []
  const priceTab = existingTabs[0]

  if (priceTab) priceTab.setAttribute('data-mobile-view-tab', 'price')
  const idleSource = existingTabs.find((btn) => !btn.matches('[aria-pressed="true"]')) || priceTab
  if (
    leftGroup &&
    idleSource &&
    priceTab &&
    !leftGroup.querySelector('[data-mobile-view-tab="orderbook"]')
  ) {
    priceTab.after(
      makeMobileTab(idleSource, 'Order Book', 'orderbook'),
      makeMobileTab(idleSource, 'Trades', 'trades'),
      makeMobileTab(idleSource, 'Depth', 'depth')
    )
  }

  const fundingTab = existingTabs.find((btn) => buttonLabel(btn) === 'Funding')
  fundingTab?.setAttribute('data-mobile-view-tab', 'funding')
  const detailsTab = existingTabs.find((btn) => buttonLabel(btn) === 'Details')
  detailsTab?.setAttribute('data-mobile-view-tab', 'details')

  if (!page.querySelector('.trade-mobile-cta')) {
    const cta = document.createElement('div')
    cta.className =
      'trade-mobile-only trade-mobile-cta fixed inset-x-0 bottom-0 z-10 flex w-full flex-col rounded-t-modal border-t-[0.5px] border-line bg-fill pb-[max(8px,env(safe-area-inset-bottom))] shadow-(--shadow-footer-top) backdrop-blur-md backdrop-saturate-150 light:bg-bg/80'
    cta.innerHTML = `<div class="flex w-full items-center gap-2 px-3 py-2.5"><button type="button" data-testid="mobile-buy-button" class="${MOBILE_CTA_BUY}"><span>Buy / Long</span></button><button type="button" data-testid="mobile-sell-button" class="${MOBILE_CTA_SELL}"><span>Sell / Short</span></button></div>`
    page.appendChild(cta)
  }

  const orderBookPanel = page.querySelector('[data-trade-panel="orderbook"]')
  const chartTypeRow = rightGroup

  const setView = (view) => {
    page.dataset.mobileView = view
    const tabs = page.querySelectorAll('[data-mobile-view-tab]')
    tabs.forEach((tab) => {
      if (tab.classList.contains('trade-desktop-only')) return
      setTabPressed(tab, tab.getAttribute('data-mobile-view-tab') === view, {
        idleClass: TAB_IDLE,
      })
    })
    if (view === 'orderbook') clickNamedButton(orderBookPanel, 'Order Book')
    if (view === 'trades') clickNamedButton(orderBookPanel, 'Trades')
    if (view === 'depth') clickNamedButton(chartTypeRow, 'Depth')
    if (view === 'price') clickNamedButton(chartTypeRow, 'TradingView')
  }

  const onClick = (event) => {
    if (dismissBillboard(event, page)) return

    const positionsTab = event.target.closest(
      '[data-trade-panel="positions"] .flex.items-stretch.gap-1 > button'
    )
    if (positionsTab && !positionsTab.classList.contains('trade-desktop-only')) {
      event.preventDefault()
      event.stopPropagation()
      selectPositionsTab(page, positionsTab)
      return
    }

    const viewTab = event.target.closest('[data-mobile-view-tab]')
    if (viewTab && !viewTab.classList.contains('trade-desktop-only')) {
      event.preventDefault()
      event.stopPropagation()
      setView(viewTab.getAttribute('data-mobile-view-tab'))
      return
    }

    const marketBtn = event.target.closest(
      'button:has([data-testid="market-selector-trigger-market-name"])'
    )
    if (marketBtn) {
      event.preventDefault()
      event.stopPropagation()
      window.dispatchEvent(new Event('lighter:open-market-selector'))
      return
    }

    const statsToggle = event.target.closest(
      '[data-testid="mobile-market-stats-toggle"]'
    )
    if (statsToggle) {
      event.preventDefault()
      event.stopPropagation()
      const open = page.dataset.statsOpen === 'true'
      page.dataset.statsOpen = open ? 'false' : 'true'
      statsToggle.setAttribute('aria-expanded', open ? 'false' : 'true')
      return
    }

    const mobileBuy = event.target.closest('[data-testid="mobile-buy-button"]')
    const mobileSell = event.target.closest('[data-testid="mobile-sell-button"]')
    if (mobileBuy || mobileSell) {
      const { connected } = getTradeState(page)
      if (!connected) {
        window.dispatchEvent(new Event('lighter:open-connect'))
        return
      }
      applyOrderSide(page, mobileSell ? 'sell' : 'buy')
      return
    }

    const connect = event.target.closest(
      'button, [data-testid="connect-wallet-button"], .cursor-pointer.underline'
    )
    if (connect) {
      const label = buttonLabel(connect)
      const isConnect =
        connect.matches('[data-testid="connect-wallet-button"]') ||
        connect.matches('[data-testid="connect-wallet-to-trade-button"]') ||
        connect.matches('[data-testid="account-connect-wallet-button"]') ||
        connect.classList.contains('underline') ||
        label === 'Connect Wallet' ||
        label === 'Connect Wallet to Trade'
      if (isConnect) {
        event.preventDefault()
        window.dispatchEvent(new Event('lighter:open-connect'))
      }
    }
  }

  page.addEventListener('click', onClick)

  return () => {
    page.removeEventListener('click', onClick)
    delete page.dataset.tradeMobileReady
  }
}

export default function Trade() {
  const [headerContainer, setHeaderContainer] = useState(null)
  const { isConnected, userAddress } = useWalletAuth()
  const { symbol: routeSymbol } = useParams()
  const symbol = (routeSymbol || 'LIT').toUpperCase()
  usePageCss('trade-page-css', '/css/lighter_trade_LIT.css')

  useEffect(() => {
    const container = document.getElementById('header-placeholder')
    if (container) {
      setHeaderContainer(container)
    }

    const page = document.querySelector('.trade-page')
    if (!page) return undefined
    page.dataset.symbol = symbol
    const cleanupMobile = setupTradeMobile(page)
    const cleanupDesktop = setupTradeDesktop(page)
    const cleanupChart = mountLivePriceChart(page, { symbol })
    const cleanupMarket = mountLiveMarketData(page, { symbol })
    const cleanupForm = mountOrderFormControls(page)
    applyWalletState(page, { isConnected, userAddress })
    return () => {
      stopPositionsSignal(page)
      cleanupMobile?.()
      cleanupDesktop?.()
      cleanupChart?.()
      cleanupMarket?.()
      page.__orderFormCleanup?.()
      cleanupForm?.()
    }
  }, [symbol])

  useEffect(() => {
    const page = document.querySelector('.trade-page')
    if (!page) return
    applyWalletState(page, { isConnected, userAddress })
  }, [isConnected, userAddress])

  return (
    <>
      <div className="trade-page-shell relative flex size-full min-h-dvh w-full flex-col items-center">
        <TradeMarkup />
      </div>
      {headerContainer && createPortal(<Header />, headerContainer)}
      <MarketSelectorSheet />
    </>
  )
}
